import { useEffect, useState } from "react";
import { Plus, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Loading, PageHeader } from "../../components/shared";


const EMPTY = {
  enabled: false,
  request_enabled: false,
  gst_mode: "NOT_APPLICABLE",
  gst_rate: "",
  inclusions: "",
  exclusions: "",
};


export default function AdminResidentServices() {
  const [catalogue, setCatalogue] = useState(null);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [optionDraft, setOptionDraft] = useState(null);
  const [optionIndex, setOptionIndex] = useState(null);

  const load = async () => {
    const response = await api.get("/admin/resident-services/catalogue");
    setCatalogue(response.data);
  };

  useEffect(() => {
    load().catch(() => setCatalogue([]));
  }, []);

  const open = async (serviceId) => {
    try {
      const response = await api.get(`/admin/services/${serviceId}`);
      setCurrent(response.data);
      setForm({
        ...EMPTY,
        ...(response.data.resident_config || {}),
        resident_offerings: response.data.resident_offerings || [],
      });
      setOptionDraft(null);
      setOptionIndex(null);
    } catch {
      toast.error("Could not load service configuration");
    }
  };

  const setup = async (item) => {
    try {
      const response = await api.post(`/admin/resident-services/catalogue/${item.key}/setup`);
      await load();
      await open(response.data.service_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not prepare this service");
    }
  };

  const save = async () => {
    if (!current) return;
    if (optionDraft) {
      toast.error("Save or discard the service option before saving this category");
      return;
    }
    try {
      await api.patch(`/admin/services/${current.service_id}`, {
        resident_config: {
          ...form,
          gst_rate: form.gst_rate === "" ? null : Number(form.gst_rate),
        },
        resident_offerings: form.resident_offerings || [],
        visibility: { ...current.visibility, resident: Boolean(form.enabled) },
      });
      toast.success("Doorstep service category saved");
      setCurrent(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save configuration");
    }
  };

  const addOption = () => {
    setOptionIndex(null);
    setOptionDraft({
      name: "",
      short_description: "",
      active: true,
      request_enabled: true,
      price_type: "FIXED",
      price_amount: "",
      use_parent_gst: true,
      gst_mode: form.gst_mode,
      gst_rate: form.gst_rate,
      inclusions: "",
      exclusions: "",
    });
  };

  const saveOption = () => {
    if (!optionDraft?.name?.trim()) {
      toast.error("Enter a service option name");
      return;
    }
    if (["FIXED", "STARTING_FROM"].includes(optionDraft.price_type)) {
      if (optionDraft.price_amount === "") {
        toast.error("Enter the authorized amount for this service option");
        return;
      }
    }
    const next = {
      ...optionDraft,
      price_amount: optionDraft.price_amount === "" ? null : Number(optionDraft.price_amount),
      gst_rate: optionDraft.gst_rate === "" ? null : Number(optionDraft.gst_rate),
    };
    const offerings = [...(form.resident_offerings || [])];
    if (optionIndex === null) offerings.push(next);
    else offerings[optionIndex] = next;
    setForm({ ...form, resident_offerings: offerings });
    setOptionDraft(null);
    setOptionIndex(null);
  };

  const editOption = (item, index) => {
    setOptionIndex(index);
    setOptionDraft({
      ...item,
      price_amount: item.price_amount ?? "",
      gst_rate: item.gst_rate ?? "",
    });
  };

  const removeOption = (index) => {
    setForm({
      ...form,
      resident_offerings: form.resident_offerings.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  if (!catalogue) return <Loading />;

  return (
    <section data-testid="admin-resident-services-page">
      <PageHeader
        title="Resident Doorstep Services"
        subtitle="Set up each service category, its options, authorized price, and tax treatment."
      />
      <div
        className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
        data-testid="admin-resident-service-list"
      >
        {catalogue.map((item) => {
          const service = item.service;
          const options = service?.resident_offerings || [];
          return (
            <article
              className="flex min-h-[210px] flex-col border border-slate-200 bg-white p-5"
              data-testid={`admin-resident-service-${item.key}`}
              key={item.key}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-700">
                Resident catalogue
              </p>
              <h2 className="mt-2 font-display text-xl font-bold text-slate-950">{item.name}</h2>
              <p className="mt-3 flex-1 text-sm text-slate-600">
                {service
                  ? `${options.length} configured option${options.length === 1 ? "" : "s"}`
                  : "Not configured yet"}
              </p>
              <Button
                className="mt-5 w-full"
                data-testid={`admin-resident-service-edit-${item.key}`}
                onClick={() => (service ? open(service.service_id) : setup(item))}
                variant="outline"
              >
                <Settings2 />
                {service ? "Configure options" : "Set up service"}
              </Button>
            </article>
          );
        })}
      </div>
      <Dialog open={Boolean(current)} onOpenChange={(open) => !open && setCurrent(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="admin-resident-service-dialog">
          <DialogHeader>
            <DialogTitle className="pr-8 font-display text-2xl">{current?.name}</DialogTitle>
            <DialogDescription>
              Configure resident visibility, category-wide GST, and the exact service options residents may select.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <Toggle
              label="Show this category in Doorstep Services"
              testId="admin-resident-service-enabled"
              value={form.enabled}
              onChange={(value) => setForm({ ...form, enabled: value })}
            />
            <Toggle
              label="Allow residents to submit its configured options"
              testId="admin-resident-service-request-enabled"
              value={form.request_enabled}
              onChange={(value) => setForm({ ...form, request_enabled: value })}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Default GST display">
                <select
                  className="form-select"
                  data-testid="admin-resident-service-gst-mode"
                  onChange={(event) => setForm({ ...form, gst_mode: event.target.value })}
                  value={form.gst_mode}
                >
                  <option value="NOT_APPLICABLE">GST not applicable</option>
                  <option value="INCLUDED">GST included</option>
                  <option value="EXTRA">GST extra</option>
                </select>
              </Field>
              <Field label="Default GST rate (%)">
                <Input
                  data-testid="admin-resident-service-gst-rate"
                  disabled={form.gst_mode !== "EXTRA"}
                  min="0"
                  onChange={(event) => setForm({ ...form, gst_rate: event.target.value })}
                  type="number"
                  value={form.gst_rate ?? ""}
                />
              </Field>
            </div>
            <div className="border-t border-slate-200 pt-5" data-testid="admin-resident-service-options">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-950">Resident service options</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Residents select one exact option; the parent category itself is not requestable.
                  </p>
                </div>
                <Button
                  data-testid="admin-resident-service-add-option"
                  disabled={Boolean(optionDraft)}
                  onClick={addOption}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <Plus /> Add option
                </Button>
              </div>
              <div className="mt-4 space-y-3">
                {(form.resident_offerings || []).map((item, index) => (
                  <div
                    className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 p-3"
                    data-testid={`admin-resident-option-${index}`}
                    key={item.offering_id || `${item.name}-${index}`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-950">{item.name}</p>
                      <p className="mt-1 text-xs text-slate-600">
                        {item.price_type === "ON_REQUEST"
                          ? "Price on request"
                          : `₹${Number(item.price_amount).toLocaleString("en-IN")}`}
                        {item.use_parent_gst ? " · Uses category GST" : " · GST override"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`admin-resident-option-edit-${index}`}
                        onClick={() => editOption(item, index)}
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        Edit
                      </Button>
                      <Button
                        data-testid={`admin-resident-option-delete-${index}`}
                        onClick={() => removeOption(index)}
                        size="icon"
                        type="button"
                        variant="outline"
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {optionDraft && (
                <OptionEditor
                  draft={optionDraft}
                  onCancel={() => {
                    setOptionDraft(null);
                    setOptionIndex(null);
                  }}
                  onChange={setOptionDraft}
                  onSave={saveOption}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="admin-resident-service-save" onClick={save}>Save configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


function Field({ children, label }) {
  return (
    <label className="block">
      <Label className="mb-1 block">{label}</Label>
      {children}
    </label>
  );
}


function Toggle({ label, onChange, testId, value }) {
  return (
    <div className="flex items-center justify-between border border-slate-200 p-3">
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <Switch checked={Boolean(value)} data-testid={testId} onCheckedChange={onChange} />
    </div>
  );
}


function OptionEditor({ draft, onCancel, onChange, onSave }) {
  const update = (key, value) => onChange({ ...draft, [key]: value });
  return (
    <div className="mt-4 space-y-4 border border-sky-200 bg-sky-50 p-4" data-testid="admin-resident-option-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Option name">
          <Input data-testid="admin-resident-option-name" onChange={(event) => update("name", event.target.value)} value={draft.name} />
        </Field>
        <Field label="Price display">
          <select className="form-select" data-testid="admin-resident-option-price-type" onChange={(event) => update("price_type", event.target.value)} value={draft.price_type}>
            <option value="FIXED">Fixed price</option>
            <option value="STARTING_FROM">Starting from</option>
            <option value="ON_REQUEST">Price on request</option>
          </select>
        </Field>
        <Field label="Authorized amount (₹)">
          <Input data-testid="admin-resident-option-price-amount" disabled={draft.price_type === "ON_REQUEST"} min="0" onChange={(event) => update("price_amount", event.target.value)} type="number" value={draft.price_amount ?? ""} />
        </Field>
        <Toggle label="Requests enabled" testId="admin-resident-option-request-enabled" value={draft.request_enabled} onChange={(value) => update("request_enabled", value)} />
      </div>
      <Toggle label="Use category GST" testId="admin-resident-option-use-parent-gst" value={draft.use_parent_gst} onChange={(value) => update("use_parent_gst", value)} />
      {!draft.use_parent_gst && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="GST display">
            <select className="form-select" data-testid="admin-resident-option-gst-mode" onChange={(event) => update("gst_mode", event.target.value)} value={draft.gst_mode}>
              <option value="NOT_APPLICABLE">GST not applicable</option>
              <option value="INCLUDED">GST included</option>
              <option value="EXTRA">GST extra</option>
            </select>
          </Field>
          <Field label="GST rate (%)">
            <Input data-testid="admin-resident-option-gst-rate" disabled={draft.gst_mode !== "EXTRA"} min="0" onChange={(event) => update("gst_rate", event.target.value)} type="number" value={draft.gst_rate ?? ""} />
          </Field>
        </div>
      )}
      <Field label="Short description">
        <Textarea data-testid="admin-resident-option-description" onChange={(event) => update("short_description", event.target.value)} value={draft.short_description} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="What is included">
          <Textarea data-testid="admin-resident-option-inclusions" onChange={(event) => update("inclusions", event.target.value)} value={draft.inclusions} />
        </Field>
        <Field label="What is excluded">
          <Textarea data-testid="admin-resident-option-exclusions" onChange={(event) => update("exclusions", event.target.value)} value={draft.exclusions} />
        </Field>
      </div>
      <div className="flex justify-end gap-2">
        <Button data-testid="admin-resident-option-cancel" onClick={onCancel} type="button" variant="outline">Cancel</Button>
        <Button data-testid="admin-resident-option-save" onClick={onSave} type="button">Save option</Button>
      </div>
    </div>
  );
}